/**
 * Profile shape mirrored on `users/{uid}`.
 *
 * Only the user-editable fields land here — role flags (`internalAdmin`),
 * org membership (`orgIds`, `activeOrgId`), and lifecycle flags
 * (`pendingActivation`) live elsewhere and are intentionally not
 * surfaced through this service.
 */
export interface UserProfile {
  readonly displayName: string | null;
  readonly photoUrl: string | null;
  readonly email: string | null;
}

/**
 * User preferences stored at `users/{uid}/private/preferences`.
 *
 * Defaults applied when the doc is missing or partially populated — the
 * service always returns a fully-shaped object so the UI never needs to
 * branch on `undefined`.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

export interface NotificationPreferences {
  readonly runCompleted: boolean;
  readonly runFailed: boolean;
}

export interface UserPreferences {
  readonly theme: ThemePreference;
  readonly language: string;
  readonly notifications: NotificationPreferences;
  /**
   * The recording pod has opened itself once, with its tutorial, on this
   * user's first live capture. Per user rather than per browser so the
   * tutorial is not repeated on a second machine.
   */
  readonly captureTutorialSeen: boolean;
}

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  theme: 'system',
  language: 'en',
  notifications: {
    runCompleted: true,
    runFailed: true,
  },
  captureTutorialSeen: false,
};
